// Pure geography helpers for the "you ↔ polling unit" distance aid. No DOM, no
// network — everything here is unit-tested in isolation.

export interface LatLng {
	lat: number;
	lng: number;
}

// Bounding box that every real Nigerian polling unit falls inside. Used to
// reject placeholder and corrupt coordinates (the backfill left some rows at
// `(0,0)` or in other hemispheres) before they ever reach a map.
export const NIGERIA_BOUNDS = { minLat: 4, maxLat: 14, minLng: 2.5, maxLng: 15 } as const;

// Under this many metres the applicant is, for all practical purposes, standing
// at the unit; the caption says so instead of quoting a tiny number.
export const AT_UNIT_METERS = 100;

const EARTH_RADIUS_METERS = 6_371_000;

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

export const isInNigeria = ({ lat, lng }: LatLng): boolean =>
	lat >= NIGERIA_BOUNDS.minLat && lat <= NIGERIA_BOUNDS.maxLat && lng >= NIGERIA_BOUNDS.minLng && lng <= NIGERIA_BOUNDS.maxLng;

// The polling-unit coordinate columns are TEXT; "unset" arrives as the literal
// strings 'null' / 'undefined' / '' rather than JSON null. Returns a finite
// number or null — never NaN.
export const parseCoordinateText = (raw: unknown): number | null => {
	if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
	if (typeof raw !== 'string') return null;
	const trimmed = raw.trim();
	if (trimmed === '' || /^(null|undefined|nan)$/i.test(trimmed)) return null;
	if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
	const value = Number(trimmed);
	return Number.isFinite(value) ? value : null;
};

/**
 * Parses a polling unit's raw `latitude` / `longitude` text into a point, or
 * null when the unit is unmapped. Rejects one-sided pairs, `(0,0)` and any point
 * outside Nigeria so a placeholder never renders as a real location.
 */
export const parsePuCoordinate = (lat: unknown, lng: unknown): LatLng | null => {
	const parsedLat = parseCoordinateText(lat);
	const parsedLng = parseCoordinateText(lng);
	if (parsedLat === null || parsedLng === null) return null;
	if (parsedLat === 0 && parsedLng === 0) return null;
	const point = { lat: parsedLat, lng: parsedLng };
	return isInNigeria(point) ? point : null;
};

/** Great-circle distance in metres (Haversine, spherical Earth). */
export const haversineMeters = (a: LatLng, b: LatLng): number => {
	const dLat = toRadians(b.lat - a.lat);
	const dLng = toRadians(b.lng - a.lng);
	const lat1 = toRadians(a.lat);
	const lat2 = toRadians(b.lat);
	const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
	return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** Rounds a coordinate to 4 decimal places (~11 m) — used for cache keys. */
export const round4 = (value: number): number => Math.round(value * 10_000) / 10_000;

const stripTrailingZero = (text: string): string => text.replace(/\.0$/, '');

/**
 * Human distance, always prefixed "About" so nobody mistakes a straight-line
 * estimate for a measured route:
 *   < 100 m   → "At the polling unit"
 *   < 1 km    → "About 350 m"
 *   < 9.95 km → "About 2.3 km"
 *   otherwise → "About 14 km"
 */
export const formatDistance = (meters: number): string => {
	if (!Number.isFinite(meters) || meters < 0) return 'Distance unavailable';
	if (meters < AT_UNIT_METERS) return 'At the polling unit';
	if (meters < 1000) return `About ${Math.round(meters)} m`;
	if (meters < 9950) return `About ${stripTrailingZero((meters / 1000).toFixed(1))} km`;
	return `About ${Math.round(meters / 1000)} km`;
};

/**
 * Walking time from a duration in seconds: "≈ 35 min walk", "≈ 1 h 5 min walk",
 * "≈ 2 h walk". Anything under a minute reads as one minute.
 */
export const formatWalk = (seconds: number): string => {
	if (!Number.isFinite(seconds) || seconds < 0) return '';
	const totalMinutes = Math.max(1, Math.round(seconds / 60));
	if (totalMinutes < 60) return `≈ ${totalMinutes} min walk`;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return minutes === 0 ? `≈ ${hours} h walk` : `≈ ${hours} h ${minutes} min walk`;
};

/**
 * Google Maps directions link to the unit in walking mode. Deliberately carries
 * NO origin: Maps uses the phone's own location, so the applicant's GPS fix
 * never leaves the device through this link.
 */
export const mapsDeepLink = (pu: LatLng): string =>
	`https://www.google.com/maps/dir/?api=1&destination=${pu.lat.toFixed(6)},${pu.lng.toFixed(6)}&travelmode=walking`;
